import React, { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Search, Sparkles, TrendingUp, Clock, Image, Video, FileText, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBrowser } from '@/contexts/BrowserContext';
import { useNavigate } from 'react-router-dom';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const { addTab } = useBrowser();
  const navigate = useNavigate();

  const searchEngines = [
    { name: '百度', url: 'https://www.baidu.com/s?wd=', icon: '🔍' },
    { name: '必应', url: 'https://www.bing.com/search?q=', icon: '🌐' },
    { name: '谷歌', url: 'https://www.google.com/search?q=', icon: '🔎' },
    { name: '知乎', url: 'https://www.zhihu.com/search?q=', icon: '📚' },
    { name: 'B站', url: 'https://search.bilibili.com/all?keyword=', icon: '📺' },
    { name: '微博', url: 'https://s.weibo.com/weibo?q=', icon: '🐦' },
  ];

  const imageSearchEngines = [
    { name: '百度图片', url: 'https://image.baidu.com/search/index?tn=baiduimage&word=' },
    { name: '必应图片', url: 'https://www.bing.com/images/search?q=' },
  ];

  const videoSearchEngines = [
    { name: 'B站', url: 'https://search.bilibili.com/all?keyword=' },
    { name: '抖音', url: 'https://www.douyin.com/search/' },
    { name: '优酷', url: 'https://so.youku.com/search_video/q_' },
  ];

  const academicSearchEngines = [
    { name: '知网', url: 'https://kns.cnki.net/kns8/defaultresult/index?kw=' },
    { name: '百度学术', url: 'https://xueshu.baidu.com/s?wd=' },
    { name: 'Google Scholar', url: 'https://scholar.google.com/scholar?q=' },
  ];

  const hotSearches = [
    'AI工具推荐',
    '自媒体运营技巧',
    '视频剪辑教程',
    '副业赚钱方法',
    '设计素材网站',
    '编程学习路线',
  ];

  const handleSearch = (engine: { url: string; name: string }) => {
    if (!query.trim()) return;
    const searchUrl = engine.url + encodeURIComponent(query.trim());
    addTab(searchUrl, `${engine.name} - ${query}`);
    navigate('/browser');
  };

  const handleQuickSearch = (keyword: string) => {
    setQuery(keyword);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 py-12 max-w-5xl">
        <div className="flex flex-col items-center text-center mb-12">
          <div className="bg-primary/10 p-4 rounded-2xl mb-4">
            <Search className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-4">全局聚合搜索</h1>
          <p className="text-muted-foreground text-lg max-w-xl">
            一键搜索全网资源，聚合多个搜索引擎结果
          </p>
        </div>

        {/* Search Input */}
        <div className="mb-8">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchEngines[0])}
                placeholder="输入搜索内容..."
                className="pl-10 h-14 text-lg"
              />
            </div>
            <Button 
              size="lg" 
              className="h-14 px-8"
              onClick={() => handleSearch(searchEngines[0])}
            >
              搜索
            </Button>
          </div>
        </div>

        {/* Hot Searches */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h3 className="font-bold">热门搜索</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {hotSearches.map((keyword, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => handleQuickSearch(keyword)}
              >
                {keyword}
              </Button>
            ))}
          </div>
        </div>

        {/* Search Engines Tabs */}
        <Tabs defaultValue="web" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="web">
              <Globe className="h-4 w-4 mr-2" />
              网页
            </TabsTrigger>
            <TabsTrigger value="image">
              <Image className="h-4 w-4 mr-2" />
              图片
            </TabsTrigger>
            <TabsTrigger value="video">
              <Video className="h-4 w-4 mr-2" />
              视频
            </TabsTrigger>
            <TabsTrigger value="academic">
              <FileText className="h-4 w-4 mr-2" />
              学术
            </TabsTrigger>
          </TabsList>

          <TabsContent value="web" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {searchEngines.map((engine, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearch(engine)}
                  className="flex items-center gap-3 p-4 bg-card border rounded-2xl hover:border-primary/50 hover:shadow-lg transition-all"
                >
                  <span className="text-3xl">{engine.icon}</span>
                  <div className="text-left">
                    <div className="font-bold">{engine.name}</div>
                    <div className="text-xs text-muted-foreground">网页搜索</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="image" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {imageSearchEngines.map((engine, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearch(engine)}
                  className="flex items-center gap-3 p-4 bg-card border rounded-2xl hover:border-primary/50 hover:shadow-lg transition-all"
                >
                  <Image className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <div className="font-bold">{engine.name}</div>
                    <div className="text-xs text-muted-foreground">图片搜索</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="video" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {videoSearchEngines.map((engine, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearch(engine)}
                  className="flex items-center gap-3 p-4 bg-card border rounded-2xl hover:border-primary/50 hover:shadow-lg transition-all"
                >
                  <Video className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <div className="font-bold">{engine.name}</div>
                    <div className="text-xs text-muted-foreground">视频搜索</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="academic" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {academicSearchEngines.map((engine, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSearch(engine)}
                  className="flex items-center gap-3 p-4 bg-card border rounded-2xl hover:border-primary/50 hover:shadow-lg transition-all"
                >
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <div className="font-bold">{engine.name}</div>
                    <div className="text-xs text-muted-foreground">学术搜索</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Tips */}
        <div className="mt-12 bg-primary/5 border border-primary/20 rounded-3xl p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            搜索技巧
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>所有搜索结果都在内置浏览器中打开，不跳转外部</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>支持多标签页同时搜索，方便对比结果</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>点击热门搜索快速填充搜索框</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>切换不同搜索引擎，获取更全面的结果</span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
